/*
 * OutreachPanel — cold-outreach hub.
 *   Tabs: Send Link | Campaigns | Log
 *
 * Fills the "how does the admin send an applicant a link" gap.
 */
import React, { useEffect, useState } from "react";
import { apiClient } from "../../lib/api";
import { toast } from "sonner";
import { autoPrefixUS } from "../../components/PhoneInput";

const C = { forest: "#2B4C3B", ink: "#0F172A", dim: "#475569", line: "#E2E0D8", amber: "#B45309" };

const TABS = [
  { id: "send", label: "Send link" },
  { id: "campaigns", label: "Campaigns & QR" },
  { id: "log", label: "Outreach log" },
];

function SendLink() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", role: "driver", note: "" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || (!form.phone.trim() && !form.email.trim())) {
      toast.error("Need name + at least a phone or email");
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const { data } = await apiClient.post("/admin/outreach/send-link", {
        name: form.name.trim(), phone: form.phone.trim() || null,
        email: form.email.trim() || null, role: form.role, note: form.note.trim() || null,
      });
      setResult(data);
      toast.success("Link sent");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} data-testid="send-link-form" className="space-y-3 max-w-xl">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-bold uppercase" style={{ color: C.dim }}>Name</label>
          <input type="text" value={form.name} onChange={e => setForm({...form, name: e.target.value})}
            data-testid="send-link-name" required
            className="w-full mt-1 px-3 py-2 border rounded-md" style={{ borderColor: C.line }} />
        </div>
        <div>
          <label className="text-xs font-bold uppercase" style={{ color: C.dim }}>Role</label>
          <select value={form.role} onChange={e => setForm({...form, role: e.target.value})}
            data-testid="send-link-role"
            className="w-full mt-1 px-3 py-2 border rounded-md" style={{ borderColor: C.line }}>
            <option value="driver">Independent Driver → /drive/new</option>
            <option value="tp">Provider (TP) → /partners/apply</option>
            <option value="rider">Rider → /ride/new</option>
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-bold uppercase" style={{ color: C.dim }}>Phone (voice call + text)</label>
          <input type="tel" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})}
            onBlur={() => setForm((f) => ({ ...f, phone: autoPrefixUS(f.phone) }))}
            data-testid="send-link-phone" placeholder="+1 555 000 1234"
            className="w-full mt-1 px-3 py-2 border rounded-md" style={{ borderColor: C.line }} />
        </div>
        <div>
          <label className="text-xs font-bold uppercase" style={{ color: C.dim }}>Email</label>
          <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})}
            data-testid="send-link-email" placeholder="them@email.com"
            className="w-full mt-1 px-3 py-2 border rounded-md" style={{ borderColor: C.line }} />
        </div>
      </div>
      <div>
        <label className="text-xs font-bold uppercase" style={{ color: C.dim }}>Note (admin only)</label>
        <input type="text" value={form.note} onChange={e => setForm({...form, note: e.target.value})}
          data-testid="send-link-note" placeholder="Called from 800# about hiring push"
          className="w-full mt-1 px-3 py-2 border rounded-md" style={{ borderColor: C.line }} />
      </div>
      <button type="submit" disabled={busy} data-testid="send-link-submit"
        className="px-6 py-3 rounded-xl font-bold text-white disabled:opacity-60"
        style={{ background: C.forest }}>
        {busy ? "Sending…" : "Send onboarding link"}
      </button>
      {result && (() => {
        const voiceOk = String(result.voice_status || "").startsWith("queued") || result.voice_status === "skipped";
        const emailOk = String(result.email_status || "").startsWith("sent") || result.email_status === "skipped";
        const smsOk = String(result.sms_status || "").startsWith("sent") || result.sms_status === "skipped";
        const allOk = voiceOk && emailOk && smsOk;
        const bg = allOk ? "#F0FDF4" : "#FEF3C7";
        const border = allOk ? "#BBF7D0" : "#FDE68A";
        const iconColor = allOk ? C.forest : "#B45309";
        return (
          <div className="mt-4 p-3 rounded-lg" style={{ background: bg, border: `1px solid ${border}` }} data-testid="outreach-result">
            <div className="text-xs font-bold" style={{ color: iconColor }}>
              {allOk ? "✅ Delivered" : "⚠ Partial delivery — see status below"}
            </div>
            <div className="text-xs mt-1" style={{ color: C.dim }}>
              <b>Voice:</b> <span style={{ color: voiceOk ? C.dim : "#B45309" }}>{result.voice_status}</span> ·{" "}
              <b>Text:</b> <span style={{ color: smsOk ? C.dim : "#B45309" }}>{result.sms_status}</span> ·{" "}
              <b>Email:</b> <span style={{ color: emailOk ? C.dim : "#B45309" }}>{result.email_status}</span>
            </div>
            {!voiceOk && String(result.voice_status || "").includes("21217") && (
              <div className="text-[11px] mt-1" style={{ color: "#B45309" }}>
                The phone number could not be dialed. It should be a US number in any of these formats:
                <code className="mx-1">(940) 923-0878</code>,
                <code className="mx-1">940-923-0878</code>,
                <code className="mx-1">9409230878</code>,
                or E.164 <code className="mx-1">+19409230878</code>.
              </div>
            )}
            <div className="text-xs mt-1 font-mono break-all" style={{ color: C.ink }}>{result.short_url}</div>
          </div>
        );
      })()}
    </form>
  );
}

function Campaigns() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ name: "", landing: "/drive/new", utm_source: "", utm_medium: "" });
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try { const { data } = await apiClient.get("/admin/outreach/campaigns"); setRows(data.items || []); } catch (_e) { /* silent */ }
  };
  useEffect(() => { load(); }, []);
  const create = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setBusy(true);
    try {
      await apiClient.post("/admin/outreach/campaigns", {
        name: form.name.trim(), landing: form.landing,
        utm_source: form.utm_source || null, utm_medium: form.utm_medium || null,
        utm_campaign: form.name.trim().toLowerCase().replace(/\s+/g, "-"),
      });
      setForm({ name: "", landing: "/drive/new", utm_source: "", utm_medium: "" });
      toast.success("Campaign created");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Create failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-6" data-testid="campaigns-tab">
      <form onSubmit={create} className="rounded-2xl p-4 border" style={{ borderColor: C.line, background: "#fff" }}>
        <div className="text-sm font-bold mb-3">Create campaign</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <input placeholder="Name (e.g. Detroit FB Ad Nov)" value={form.name} onChange={e => setForm({...form, name: e.target.value})}
            data-testid="campaign-name" required
            className="col-span-2 px-3 py-2 border rounded-md" style={{ borderColor: C.line }} />
          <select value={form.landing} onChange={e => setForm({...form, landing: e.target.value})}
            className="px-3 py-2 border rounded-md" style={{ borderColor: C.line }}>
            <option value="/drive/new">/drive/new (Driver)</option>
            <option value="/partners/apply">/partners/apply (TP)</option>
            <option value="/ride/new">/ride/new (Rider)</option>
          </select>
          <button type="submit" disabled={busy} data-testid="campaign-create"
            className="px-4 py-2 rounded-md font-bold text-white disabled:opacity-60" style={{ background: C.forest }}>
            {busy ? "…" : "Create"}
          </button>
        </div>
      </form>
      {rows.length === 0 ? (
        <div className="p-8 text-sm text-center" style={{ color: C.dim }}>No campaigns yet. Create one above.</div>
      ) : (
        <ul className="space-y-2">
          {rows.map(c => (
            <li key={c.id} className="rounded-lg border p-3 flex items-center justify-between" style={{ borderColor: C.line, background: "#fff" }}>
              <div className="min-w-0">
                <div className="font-semibold text-sm">{c.name}</div>
                <div className="text-xs font-mono break-all" style={{ color: C.dim }}>{c.short_url}</div>
                <div className="text-[11px]" style={{ color: C.dim }}>Landing: {c.landing} · Scans: {c.scans} · Conversions: {c.conversions}</div>
              </div>
              <div className="flex gap-2">
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/outreach/campaigns/${c.slug}/qr`} target="_blank" rel="noreferrer"
                  data-testid={`campaign-qr-${c.slug}`}
                  className="px-3 py-1.5 rounded-md text-xs font-bold text-white" style={{ background: C.forest }}>QR ↗</a>
                <button onClick={() => { navigator.clipboard.writeText(c.short_url); toast.success("URL copied"); }}
                  className="px-3 py-1.5 rounded-md text-xs font-semibold border" style={{ borderColor: C.line }}>Copy URL</button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Log() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    (async () => {
      try { const { data } = await apiClient.get("/admin/outreach/log"); setRows(data.items || []); } catch (_e) { /* silent */ }
    })();
  }, []);
  return (
    <div data-testid="outreach-log" className="rounded-2xl bg-white border overflow-hidden" style={{ borderColor: C.line }}>
      {rows.length === 0 ? (
        <div className="p-6 text-sm text-center" style={{ color: C.dim }}>No outreach sent yet.</div>
      ) : (
        <table className="w-full text-sm">
          <thead style={{ background: "#F5F3EA" }}>
            <tr className="text-left" style={{ color: C.dim }}>
              <th className="p-3 text-xs uppercase">When</th>
              <th className="p-3 text-xs uppercase">Who</th>
              <th className="p-3 text-xs uppercase">Role</th>
              <th className="p-3 text-xs uppercase">Voice</th>
              <th className="p-3 text-xs uppercase">Email</th>
              <th className="p-3 text-xs uppercase">Link</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} className="border-t" style={{ borderColor: C.line }}>
                <td className="p-3 text-xs">{(r.created_at || "").slice(0, 16).replace("T", " ")}</td>
                <td className="p-3"><b>{r.name}</b><br/><span className="text-[11px]" style={{ color: C.dim }}>{r.email || r.phone}</span></td>
                <td className="p-3 text-xs">{r.role}</td>
                <td className="p-3 text-[11px]">{r.voice_status}</td>
                <td className="p-3 text-[11px]">{r.email_status}</td>
                <td className="p-3 text-[11px] font-mono break-all">{r.short_url}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default function OutreachPanel() {
  const [tab, setTab] = useState("send");
  return (
    <div className="space-y-4" data-testid="outreach-panel">
      <div>
        <h1 className="text-2xl font-bold" style={{ color: C.ink }}>Outreach</h1>
        <div className="text-sm" style={{ color: C.dim }}>Send an application link to a caller. Generate campaign QR codes for hiring pushes.</div>
      </div>
      <div className="flex gap-2">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            data-testid={`outreach-tab-${t.id}`}
            className="px-4 py-2 rounded-full text-sm font-semibold"
            style={{
              background: tab === t.id ? C.forest : "#fff",
              color: tab === t.id ? "#fff" : C.ink,
              border: `1px solid ${tab === t.id ? C.forest : C.line}`,
            }}>{t.label}</button>
        ))}
      </div>
      {tab === "send" && <SendLink />}
      {tab === "campaigns" && <Campaigns />}
      {tab === "log" && <Log />}
    </div>
  );
}
